import * as Sentry from '@sentry/react';
import { sonner } from '@sr/ui-library';
import { UseQueryResult } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { convertStringToSentryTag } from '../../utils/helpers/common';
import { ErrorFallback } from './ErrorFallback';
import { LoadingFallback } from './LoadingFallback';

const { toast } = sonner;

// Single query props
interface SingleQueryProps<T extends UseQueryResult> {
  children: ((data: NonNullable<T['data']>) => ReactNode) | ReactNode;
  errorFallback?: (error: T['error']) => ReactNode;
  loadingFallback?: ReactNode;
  notFetched?: ReactNode;
  query: T;
}

/**
 * SingleQueryResults accepts a query results object from React Query and invokes
 * its children with the resolved data when available. Optional props allow for
 * supplying custom UI elements for error, loading, or not-fetched states.
 *
 * `data` is type-safe and guaranteed to be defined. Example:
 * ```tsx
 * <SingleQueryResults query={query}>
 *   {(data) => (
 *     <div>
 *       {data.name}
 *     </div>
 *   )}
 * </SingleQueryResults>
 * ```
 */
export const SingleQueryResults = <T extends UseQueryResult>({
  children,
  errorFallback,
  loadingFallback,
  notFetched,
  query,
}: SingleQueryProps<T>) => {
  const { data, error, fetchStatus, status } = query;

  const hasData = data !== undefined && data !== null;

  /**
   * Log error(s) to Sentry
   */
  if (status === 'error') {
    Sentry.captureException(error, {
      tags: {
        type: convertStringToSentryTag('SingleQueryResults'),
      },
    });
  }

  /**
   * This only shows an error if the data for a query is undefined.
   * If there is data in the cache, then a previous query likely
   * succeeded and the error is due to a refetch.
   *
   * In this case we can log the error (above),
   * but avoid disrupting the UI.
   */
  if (status === 'error' && !hasData) {
    if (error.message) {
      toast.error('An error occurred', {
        description: error.message,
      });
    }

    return errorFallback ? (
      errorFallback(error)
    ) : (
      <ErrorFallback error={error} />
    );
  }

  /**
   * Loading query for the first time
   */
  if (status === 'pending' && fetchStatus === 'fetching') {
    return loadingFallback ?? <LoadingFallback />;
  }

  /**
   * Query hasn't been fetched yet and no initial data was set.
   */
  if (status === 'pending' && fetchStatus !== 'fetching' && !hasData) {
    return notFetched ?? null;
  }

  if (hasData) {
    return typeof children === 'function' ? children(data) : children;
  }

  Sentry.captureException(new Error('No data returned from API'), {
    tags: {
      type: convertStringToSentryTag('SingleQueryResults'),
    },
  });

  return null;
};
